import type { User } from "firebase/auth";
import { useCallback, useEffect, useMemo, useState } from "react";

import { gameApi, friendlyError } from "../lib/api";
import { auth } from "../lib/firebase";
import { defaultPlayerAvatar } from "../lib/playerAvatar";
import { defaultPlayerName } from "../lib/playerName";
import { cachePlayerProfile, readCachedPlayerProfile } from "../lib/playerProfileCache";
import type { PlayerProfile } from "../lib/types";

type ProfileState = { uid: string; profile: PlayerProfile | null; ready: boolean; error: string };

// Keep account data scoped to the current identity, including during account switches.
export function usePlayerProfile(user: User | null | undefined) {
  const uid = user && !user.isAnonymous ? user.uid : null;
  const [retryCount, setRetryCount] = useState(0);
  const cached = useMemo(() => uid ? readCachedPlayerProfile(uid) : null, [uid]);
  const [state, setState] = useState<ProfileState | null>(null);
  const retry = useCallback(() => { setRetryCount((count) => count + 1); }, []);

  useEffect(() => {
    if (!uid) { setState(null); return; }
    const saved = readCachedPlayerProfile(uid);
    setState((current) => current?.uid === uid && current.ready ? { ...current, error: "" }
      : { uid, profile: saved?.profile ?? null, ready: saved !== null, error: "" });
    let active = true;
    void gameApi.getPlayerProfile(retryCount ? { force: true } : undefined).then(({ profile }) => {
      if (active && auth.currentUser?.uid === uid) {
        cachePlayerProfile(uid, profile);
        setState({ uid, profile, ready: true, error: "" });
      }
    }).catch((error: unknown) => {
      if (active && auth.currentUser?.uid === uid) {
        setState((current) => ({
          uid, profile: current?.uid === uid ? current.profile : null,
          ready: current?.uid === uid && current.ready,
          error: friendlyError(error),
        }));
      }
    });
    return () => { active = false; };
  }, [uid, retryCount]);

  const current = uid && state?.uid === uid ? state
    : uid && cached ? { uid, profile: cached.profile, ready: true, error: "" } : null;
  const profile = current?.profile ?? null;
  return {
    profile,
    name: profile?.name ?? defaultPlayerName(user?.displayName),
    // Keep the last saved avatar visible while refreshing, including offline.
    // Without a cached profile, wait before falling back to the Google photo.
    avatar: uid && !current?.ready ? null : profile?.avatar ?? defaultPlayerAvatar(user),
    loading: Boolean(uid && !current?.ready && !current?.error),
    error: current?.error ?? "",
    retry,
  };
}
